import { Formik } from "formik";
import React, { useRef, useState } from "react";
import CMSFormSelect from "../formFields/CMSFormSelect";
import CMSFormTelephone from "../formFields/CMSFormTelephone";
import CMSFormInput from "../formFields/CMSFormInput";
import CMSFormTextArea from "../formFields/CMSFormTextArea";
import { contactUsSchema } from "../utils/validations";
import GeneralButton from "../common/GeneralButton";
import axios from "../../config/axios";
import ContainerLoading from "../common/loading/ContainerLoading";
import success from "../../assets/images/success.png";
import { useToast } from "../contexts/ToastContext";
import { useGeneral } from "../contexts/GeneralContext";
import { formatToLabelValue, formatToSameLabelValue } from "../utils/commonHelper";

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "success"
  const { extraContent } = useGeneral();
  const [error, setError] = useState("");
  const { addToast } = useToast();
  const refs = {
    firstName: useRef(null),
    email: useRef(null),
    country: useRef(null),
    phone: useRef(null),
    message: useRef(null),
  };

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    setTimeout(() => {
      el.focus?.({ preventScroll: true });
    }, 500);
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    setLoading(true);
    try {
      await axios.post("/save-contact-us", values);
      setStatus("success"); // show success message
      resetForm();

      // hide success message after 5 seconds
      setTimeout(() => setStatus("idle"), 10000);
    } catch (err) {
      console.error(err);
      setError("Submission failed. Please try again."); // optional alert
      setTimeout(() => {
        setError("");
      }, 5000);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`${
        status === "success" ? "bg-secondary" : "bg-[#DEE2DD]/40"
      } relative w-full  rounded-[15px]  p-8 md:p-12`}
    >
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-[15px] z-10">
          <ContainerLoading />
        </div>
      )}

      {status === "success" && (
        <div className="grid grid-cols-1 gap-8 items-start ">
          <div className="w-full rounded-[15px] p-3 lg:p-10  bg-green-50 flex items-center justify-center">
            <div className="grid grid-cols-1 2xl:grid-cols-[2.5fr_1fr] gap-6 xl:gap-12 items-center">
              <div className="flex flex-col justify-center order-2 2xl:order-1">
                <p className="text-white text-28pt md:text-48pt leading-none mb-8 text-center 2xl:text-left">
                  Submission Successful
                </p>
                <p className="text-orange text-[25px] md:text-[34px] leading-none text-center 2xl:text-left mb-8">
                  Thank you! Your message has been submitted successfully. We
                  will get back to you shortly.
                </p>
              </div>
              <div className="flex items-center justify-center order-1 2xl:order-2">
                <img
                  src={success}
                  alt="success"
                  className="w-[80px] h-[80px] lg:w-[100px] lg:h-[100px]"
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {status != "success" && (
        <Formik
          initialValues={{
            name: "",
            email: "",
            country: "",
            mobile: "",
            message: "",
          }}
          validationSchema={contactUsSchema}
          onSubmit={handleSubmit}
        >
          {(form) => (
            <div className="w-full grid grid-cols-1 gap-4 ">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <CMSFormInput
                  ref={refs.firstName}
                  className="1"
                  name="name"
                  label="Full Name"
                  form={form}
                  required
                />
              </div>

              <div className="mt-4">
                <CMSFormInput
                  ref={refs.email}
                  className="1"
                  name="email"
                  label="Email"
                  form={form}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <CMSFormSelect
                  ref={refs.country}
                  name="country"
                  label="Country"
                  className="1"
                  placeholder={{ label: "Country", value: "" }}
                  form={form}
                  onChange={form.handleChange}
                  required
                  options={formatToSameLabelValue(extraContent?.countries)}
                />
                <CMSFormTelephone
                  ref={refs["phone"]}
                  name="mobile"
                  label="Phone"
                  form={form}
                  required={true}
                />
              </div>

              <div className="mt-4">
                <CMSFormTextArea
                  ref={refs.message}
                  name="message"
                  label="Message"
                  maxLength={300}
                  form={form}
                  required
                />
              </div>
              {error && (
                <div
                  className={`relative transition-all duration-300 ease-in-out ${
                    error
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-2"
                  }`}
                >
                  {error && <span className="errorMessage">{error}</span>}
                </div>
              )}
              <div className="mt-4 flex items-end justify-end">
                <GeneralButton
                  text={"Submit"}
                  type={"submit"}
                  title="Click to submit"
                  className="text-lg px-4"
                  onClick={() => {
                    form.handleSubmit();
                    setTimeout(() => scrollToFirstError(form?.errors), 100);
                  }}
                />
              </div>
            </div>
          )}
        </Formik>
      )}
    </div>
  );
};

export default ContactForm;
